import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { calendarQuery } from '@/features/courses/api'
import { formatDay } from '@/features/courses/format'
import { subjectDotStyle } from '@/features/timetable/week'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
function mondayOf(d: Date): Date {
  const m = new Date(d)
  m.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return m
}

/** The week's course sessions, every group. */
export function CourseCalendarPage() {
  const { t } = useTranslation('courses')
  useDocumentTitle('Course calendar')
  const [monday, setMonday] = useState(() => mondayOf(new Date()))
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    return iso(d)
  })
  const sessions = useQuery(calendarQuery(days[0] ?? '', days[6] ?? ''))
  const shift = (weeks: number) =>
    setMonday((m) => {
      const n = new Date(m)
      n.setDate(m.getDate() + weeks * 7)
      return n
    })
  const today = iso(new Date())

  return (
    <>
      <PageHeader
        title={t('Course calendar')}
        breadcrumbs={[
          { label: t('Courses') },
          { label: t('Scheduled courses'), to: '/admin/courses/runs' },
          { label: t('Calendar') },
        ]}
        homeHref="/admin"
        actions={
          <div className="flex items-center gap-1">
            <IconButton
              label={t('Previous week')}
              icon={<ChevronLeft />}
              variant="secondary"
              onClick={() => shift(-1)}
            />
            <Button variant="secondary" onClick={() => setMonday(mondayOf(new Date()))}>
              {t('This week')}
            </Button>
            <IconButton
              label={t('Next week')}
              icon={<ChevronRight />}
              variant="secondary"
              onClick={() => shift(1)}
            />
          </div>
        }
      />
      {sessions.isPending ? (
        <LoadingState />
      ) : sessions.error ? (
        <ErrorState onRetry={() => void sessions.refetch()} />
      ) : (
        <div className="grid gap-3 md:grid-cols-7">
          {days.map((day) => {
            const list = sessions.data.filter((s) => s.date === day)
            return (
              <Card key={day} className={cn('min-h-32 p-3', day === today && 'ring-2 ring-brand-300')}>
                <p
                  className={cn(
                    'mb-2 text-xs font-semibold',
                    day === today ? 'text-brand-700' : 'text-muted',
                  )}
                >
                  {formatDay(day)}
                </p>
                {list.length === 0 ? (
                  <p className="text-xs text-subtle">—</p>
                ) : (
                  <ul className="space-y-2">
                    {list.map((s) => (
                      <li key={`${s.runId}-${s.startsAt}`}>
                        <Link
                          to={`/admin/courses/runs/${s.runId}`}
                          className="block rounded-control border border-line p-2 text-xs hover:border-brand-300"
                        >
                          <span className="flex items-center gap-1.5 font-semibold text-ink">
                            <span
                              className="size-2 shrink-0 rounded-full bg-brand-500"
                              style={subjectDotStyle(s.course.subject.color)}
                              aria-hidden="true"
                            />
                            <span className="truncate">{s.course.name}</span>
                          </span>
                          <span className="block text-muted tabular-nums">
                            {s.startsAt}–{s.endsAt} · {s.section.label}
                          </span>
                          {s.teacher || s.room ? (
                            <span className="block truncate text-subtle">
                              {[s.teacher?.name, s.room].filter(Boolean).join(' · ')}
                            </span>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </>
  )
}
